import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const UserProfileDropdown = ({ user, onSignOut, onSignIn }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await onSignOut();
      setIsOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // If user is not authenticated, show sign-in button
  if (!user || user.provider === 'anonymous') {
    return (
      <Button
        onClick={onSignIn}
        variant="outline"
        className="flex items-center gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
          <polyline points="10 17 15 12 10 7"></polyline>
          <line x1="15" y1="12" x2="3" y2="12"></line>
        </svg>
        Sign In
      </Button>
    );
  }

  // Get provider display name and color
  const getProviderInfo = (provider) => {
    switch (provider) {
      case 'google':
        return { name: 'Google', color: 'bg-blue-500' };
      case 'apple':
        return { name: 'Apple', color: 'bg-gray-800' };
      default:
        return { name: 'Guest', color: 'bg-gray-400' };
    }
  };

  const providerInfo = getProviderInfo(user.provider);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="ghost"
        className="flex items-center gap-2 p-2 h-auto"
      >
        {/* Profile picture or avatar */}
        {user.profile_picture ? (
          <img
            src={user.profile_picture}
            alt={user.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
            {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
          </div>
        )}
        
        {/* User name */}
        <span className="text-sm font-medium max-w-32 truncate">
          {user.name || user.email}
        </span>
        
        {/* Dropdown arrow */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6"></path>
        </svg>
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <Card className="absolute right-0 top-full mt-2 w-64 z-50 shadow-lg">
          <CardContent className="p-4 space-y-4">
            {/* User info */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-medium">
                    {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {user.name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>
              </div>
              
              {/* Provider badge */}
              <div className="flex items-center gap-2">
                <Badge 
                  variant="secondary" 
                  className={`text-xs ${providerInfo.color} text-white border-none`}
                >
                  {providerInfo.name}
                </Badge>
                {user.email_verified && (
                  <Badge variant="outline" className="text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Verified
                  </Badge>
                )}
              </div>
            </div>
            
            <hr className="border-border" />
            
            {/* Account info */}
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>
                Member since {new Date(user.created_at * 1000).toLocaleDateString()}
              </p>
            </div>
            
            <hr className="border-border" />
            
            {/* Actions */}
            <div className="space-y-2">
              <Button
                onClick={handleSignOut}
                variant="ghost"
                className="w-full justify-start text-sm h-8"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserProfileDropdown;